import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './lightbox-zoom.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

export const zoomLevels = [1.6, 2.4] as const

const zoomAnimationDurationMs = 300

@customElement('csc-lightbox-zoom')
export class CSCLightboxZoom extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String })
  source = ''

  @property({ type: String })
  alt = ''

  @property({ type: String, attribute: 'fallback-source' })
  fallbackSource = ''

  @state()
  private zoomStep = 0

  @state()
  private displayScale = 1

  @state()
  private translateX = 0

  @state()
  private translateY = 0

  @state()
  private anchorXPercent = 50

  @state()
  private anchorYPercent = 50

  @query('.csc-lightbox-zoom')
  private container!: HTMLElement

  private initialPinchDistance = 0
  private zoomAnimationFrame: number | null = null

  private get zoomed(): boolean {
    return this.zoomStep > 0
  }

  private get currentZoomLevel(): number {
    return zoomLevels[this.zoomStep - 1] ?? 1
  }

  private onClick(event: MouseEvent) {
    if (this.zoomStep >= zoomLevels.length) {
      this.zoomStep = 0
      this.updateAnchor(event.clientX, event.clientY)
      this.animateZoomTo(1)
      return
    }

    this.zoomStep += 1
    this.updateAnchor(event.clientX, event.clientY)
    this.animateZoomTo(this.currentZoomLevel)
  }

  private onMouseMove(event: MouseEvent) {
    if (!this.zoomed || !this.container) return
    this.stopZoomAnimation()
    this.updatePanPosition(event.clientX, event.clientY)
  }

  private updateAnchor(clientX: number, clientY: number) {
    if (!this.container) return

    const rect = this.container.getBoundingClientRect()
    this.anchorXPercent = clamp((clientX - rect.left) / rect.width, 0, 1) * 100
    this.anchorYPercent = clamp((clientY - rect.top) / rect.height, 0, 1) * 100
  }

  private updatePanPosition(clientX: number, clientY: number) {
    if (!this.container) return

    const rect = this.container.getBoundingClientRect()
    const xPercent = clamp((clientX - rect.left) / rect.width, 0, 1)
    const yPercent = clamp((clientY - rect.top) / rect.height, 0, 1)

    const maxTranslateX = rect.width * (this.displayScale - 1)
    const maxTranslateY = rect.height * (this.displayScale - 1)

    this.translateX = -(xPercent * maxTranslateX)
    this.translateY = -(yPercent * maxTranslateY)
  }

  private stopZoomAnimation() {
    if (this.zoomAnimationFrame !== null) {
      cancelAnimationFrame(this.zoomAnimationFrame)
      this.zoomAnimationFrame = null
    }
  }

  private animateZoomTo(targetScale: number) {
    if (!this.container) return

    this.stopZoomAnimation()

    const rect = this.container.getBoundingClientRect()
    const startScale = this.displayScale
    let startTime: number | null = null

    const tick = (now: number) => {
      if (startTime === null) startTime = now

      const progress = clamp((now - startTime) / zoomAnimationDurationMs, 0, 1)
      const eased = easeOutCubic(progress)
      const scale = startScale + (targetScale - startScale) * eased

      this.displayScale = scale
      this.translateX = -((this.anchorXPercent / 100) * rect.width * (scale - 1))
      this.translateY = -((this.anchorYPercent / 100) * rect.height * (scale - 1))

      this.zoomAnimationFrame = progress < 1 ? requestAnimationFrame(tick) : null
    }

    this.zoomAnimationFrame = requestAnimationFrame(tick)
  }

  private onTouchStart(event: TouchEvent) {
    if (event.touches.length === 2) {
      event.preventDefault()
      this.initialPinchDistance = this.getPinchDistance(event.touches)
    }
  }

  private onTouchMove(event: TouchEvent) {
    if (event.touches.length === 2 && this.initialPinchDistance > 0) {
      event.preventDefault()
      const currentDistance = this.getPinchDistance(event.touches)
      const scale = currentDistance / this.initialPinchDistance

      if (scale > 1.3 && this.zoomStep === 0) {
        const t0 = event.touches[0]
        const t1 = event.touches[1]
        if (t0 && t1) {
          this.zoomStep = 1
          const centerX = (t0.clientX + t1.clientX) / 2
          const centerY = (t0.clientY + t1.clientY) / 2
          this.updateAnchor(centerX, centerY)
          this.animateZoomTo(this.currentZoomLevel)
        }
      } else if (scale < 0.7 && this.zoomed) {
        const t0 = event.touches[0]
        const t1 = event.touches[1]
        if (t0 && t1) {
          this.updateAnchor((t0.clientX + t1.clientX) / 2, (t0.clientY + t1.clientY) / 2)
        }
        this.zoomStep = 0
        this.animateZoomTo(1)
      }
    } else if (event.touches.length === 1 && this.zoomed) {
      event.preventDefault()
      this.stopZoomAnimation()
      const t0 = event.touches[0]
      if (t0) this.updatePanPosition(t0.clientX, t0.clientY)
    }
  }

  private onTouchEnd() {
    this.initialPinchDistance = 0
  }

  private getPinchDistance(touches: TouchList): number {
    const a = touches[0]
    const b = touches[1]
    if (!a || !b) return 0
    const dx = a.clientX - b.clientX
    const dy = a.clientY - b.clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  private resetZoom() {
    this.stopZoomAnimation()
    this.zoomStep = 0
    this.displayScale = 1
    this.translateX = 0
    this.translateY = 0
    this.anchorXPercent = 50
    this.anchorYPercent = 50
    this.initialPinchDistance = 0
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)
    if (changedProperties.has('source')) {
      this.resetZoom()
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this.stopZoomAnimation()
  }

  override render() {
    const transform =
      `scale(${this.displayScale}) translate(${this.translateX / this.displayScale}px, ${this.translateY / this.displayScale}px)`

    return html`
      <div
        class="${classMap({
          'csc-lightbox-zoom': true,
          'zoomed': this.zoomed,
          [`zoom-step-${this.zoomStep}`]: this.zoomed,
        })}"
        @click="${this.onClick}"
        @mousemove="${this.onMouseMove}"
        @touchstart="${this.onTouchStart}"
        @touchmove="${this.onTouchMove}"
        @touchend="${this.onTouchEnd}"
      >
        <cse-image
          source="${this.source}"
          alt="${this.alt}"
          fit="contain"
          loading="lazy"
          fallback-source="${this.fallbackSource || nothing}"
          style="transform: ${transform}"
        ></cse-image>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-lightbox-zoom': CSCLightboxZoom
  }
}
